// frontend/src/pages/Business.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getPrimaryNavRole } from '../config/navigation';
import axios from '../services/axios';
import toast from 'react-hot-toast';
import { 
    BuildingOfficeIcon, 
    PlusIcon,
    MapPinIcon
} from '@heroicons/react/24/outline';
import PageHeader from '../components/common/PageHeader';
import EmptyState from '../components/common/EmptyState';

const Business = ({ embedded = false }) => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [businesses, setBusinesses] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const isAdminView = getPrimaryNavRole(user?.roles || user?.Roles) === 'admin';

    useEffect(() => {
        loadBusinesses();
    }, [user]);

    const loadBusinesses = async () => {
        try {
            setLoading(true);
            const endpoint = isAdminView ? '/business/admin/all' : '/business/my-businesses';
            const response = await axios.get(endpoint);
            setBusinesses(response.data.data);
        } catch (error) {
            toast.error('Failed to load businesses');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateBusiness = async (data) => {
        try {
            await axios.post('/business', data);
            toast.success('Business created successfully');
            setShowForm(false);
            loadBusinesses();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to create business');
        }
    };

    if (loading) {
        return (
            <div className="space-y-4" role="status" aria-live="polite">
                <div className="skeleton h-10 w-56" />
                <div className="skeleton h-40" />
                <div className="skeleton h-24" />
                <span className="sr-only">Loading businesses…</span>
            </div>
        );
    }

    return (
        <div className="w-full space-y-6">
            {embedded ? (
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm text-ink-600">Work with real client businesses to gain practical experience.</p>
                    <button type="button" onClick={() => setShowForm(!showForm)} className="btn-primary">
                        <PlusIcon className="w-5 h-5" aria-hidden="true" />
                        Add Business
                    </button>
                </div>
            ) : (
                <PageHeader
                    title="Client Project"
                    description="Work with real client businesses to gain practical experience"
                    actions={
                        <button type="button" onClick={() => setShowForm(!showForm)} className="btn-primary">
                            <PlusIcon className="w-5 h-5" aria-hidden="true" />
                            Add Business
                        </button>
                    }
                />
            )}

            {businesses.length === 0 && !showForm && (
                <EmptyState
                    icon={BuildingOfficeIcon}
                    title="No business yet"
                    description="Start by finding a local business to work with for your internship."
                    action={
                        <button type="button" onClick={() => setShowForm(true)} className="btn-primary">
                            Add Your First Business
                        </button>
                    }
                />
            )}

            {/* Business List */}
            {businesses.length > 0 && (
                <div className="space-y-4">
                    {businesses.map((business) => (
                        <BusinessCard 
                            key={business.id} 
                            business={business}
                            onUpdate={loadBusinesses}
                        />
                    ))}
                </div>
            )}

            {/* Create Business Form */}
            {showForm && (
                <div className="card p-6">
                    <h2 className="text-lg font-semibold text-ink-950 mb-4">Add New Business</h2>
                    <BusinessForm 
                        onSubmit={handleCreateBusiness}
                        onCancel={() => setShowForm(false)}
                    />
                </div>
            )}
        </div>
    );
};

const BusinessCard = ({ business, onUpdate }) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <div className="card overflow-hidden">
            <div 
                className="p-4 cursor-pointer hover:bg-ink-50 transition-colors"
                onClick={() => setExpanded(!expanded)}
            >
                <div className="flex items-start justify-between">
                    <div className="flex-1">
                        <div className="flex items-center space-x-2">
                            <BuildingOfficeIcon className="w-5 h-5 text-ink-400" />
                            <h3 className="font-medium text-ink-950">{business.name}</h3>
                            <span className="text-xs px-2 py-0.5 bg-ink-100 rounded-full capitalize">
                                {business.status}
                            </span>
                        </div>
                        {business.location && (
                            <p className="text-sm text-ink-600 mt-1 flex items-center space-x-1">
                                <MapPinIcon className="w-4 h-4" />
                                <span>{business.location}</span>
                            </p>
                        )}
                        {business.industry && (
                            <p className="text-sm text-ink-500">{business.industry}</p>
                        )}
                    </div>
                    <div className="flex items-center space-x-2">
                        {business.assessments && business.assessments.length > 0 && (
                            <span className="text-xs text-green-600">✅ Assessment Started</span>
                        )}
                        <span className="text-ink-400">{expanded ? '▲' : '▼'}</span>
                    </div>
                </div>
            </div>

            {expanded && (
                <div className="border-t border-border p-4 bg-ink-50">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div className="text-center">
                            <p className="text-xs text-ink-500">Status</p>
                            <p className="text-sm font-medium text-ink-950 capitalize">
                                {business.status}
                            </p>
                        </div>
                        <div className="text-center">
                            <p className="text-xs text-ink-500">Employees</p>
                            <p className="text-sm font-medium text-ink-950">
                                {business.employees || 'N/A'}
                            </p>
                        </div>
                        <div className="text-center">
                            <p className="text-xs text-ink-500">Assessments</p>
                            <p className="text-sm font-medium text-ink-950">
                                {business.assessments?.length || 0}
                            </p>
                        </div>
                        <div className="text-center">
                            <p className="text-xs text-ink-500">Actions</p>
                            <Link 
                                to={`/business/${business.id}`}
                                className="text-xs text-brand-700 font-semibold hover:underline"
                            >
                                View Details →
                            </Link>
                        </div>
                    </div>

                    {business.assessments && business.assessments.length > 0 && (
                        <div className="mt-2">
                            <p className="text-xs text-ink-500 mb-1">Latest Assessment</p>
                            <p className="text-sm text-ink-700">
                                {business.assessments[0].title || 'Untitled Assessment'}
                            </p>
                            <p className="text-xs text-ink-500 capitalize">
                                Status: {business.assessments[0].status}
                            </p>
                        </div>
                    )}

                    <div className="mt-3 flex space-x-2">
                        <Link
                            to={`/business/${business.id}/assessment`}
                            className="px-3 py-1 text-sm bg-brand-700 text-white rounded-md hover:bg-brand-800 transition-colors"
                        >
                            {business.assessments?.length > 0 ? 'Update Assessment' : 'Start Assessment'}
                        </Link>
                        <Link
                            to={`/business/${business.id}/process-maps`}
                            className="btn-secondary !min-h-[36px] !py-1.5 text-sm"
                        >
                            Process Maps
                        </Link>
                        <button
                            onClick={() => {
                                axios.post('/student/quick-action', {
                                    action: 'view_business',
                                    targetType: 'business',
                                    targetId: business.id
                                }).catch(console.error);
                                toast.success('Interaction logged');
                            }}
                            className="px-3 py-1 text-sm text-ink-600 hover:text-ink-950"
                        >
                            📝 Log Interaction
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

const BusinessForm = ({ onSubmit, onCancel }) => {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        industry: '',
        location: '',
        website: '',
        description: '',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        employees: '',
        consent_obtained: false
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.consent_obtained) {
            toast.error('Business consent is required before submission');
            return;
        }
        setLoading(true);
        try {
            await onSubmit(formData);
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-ink-700">
                        Business Name *
                    </label>
                    <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="input-field"
                        placeholder="ABC Manufacturing"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-ink-700">
                        Industry
                    </label>
                    <input
                        type="text"
                        value={formData.industry}
                        onChange={(e) => setFormData({...formData, industry: e.target.value})}
                        className="input-field"
                        placeholder="Manufacturing"
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-ink-700">
                        Location
                    </label>
                    <input
                        type="text"
                        value={formData.location}
                        onChange={(e) => setFormData({...formData, location: e.target.value})}
                        className="input-field"
                        placeholder="Karachi, Pakistan"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-ink-700">
                        Website
                    </label>
                    <input
                        type="url"
                        value={formData.website}
                        onChange={(e) => setFormData({...formData, website: e.target.value})}
                        className="input-field"
                        placeholder="https://..."
                    />
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-ink-700">
                    Description
                </label>
                <textarea
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="input-field"
                    placeholder="Describe the business..."
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-ink-700">
                        Contact Name
                    </label>
                    <input
                        type="text"
                        value={formData.contact_name}
                        onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                        className="input-field"
                        placeholder="Ahmed Alharbi"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-ink-700">
                        Contact Email
                    </label>
                    <input
                        type="email"
                        value={formData.contact_email}
                        onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                        className="input-field"
                        placeholder="contact@company.com"
                    />
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-ink-700">
                    Contact Phone
                </label>
                <input
                    type="tel"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                    className="input-field"
                    placeholder="+92 300 1234567"
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-ink-700">
                    Number of Employees
                </label>
                <input
                    type="number"
                    value={formData.employees}
                    onChange={(e) => setFormData({...formData, employees: e.target.value})}
                    className="input-field"
                    placeholder="50"
                />
            </div>

            <div className="flex items-center space-x-2">
                <input
                    type="checkbox"
                    required
                    checked={formData.consent_obtained}
                    onChange={(e) => setFormData({...formData, consent_obtained: e.target.checked})}
                    className="h-4 w-4 text-black focus:ring-black border-gray-300 rounded"
                />
                <label className="text-sm font-medium text-ink-700">
                    I have obtained consent from the business to participate *
                </label>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-border">
                <button
                    type="button"
                    onClick={onCancel}
                    className="px-4 py-2 text-ink-700 hover:bg-ink-50 rounded-md transition-colors"
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={loading || !formData.name || !formData.consent_obtained}
                    className="btn-primary"
                >
                    {loading ? 'Creating...' : 'Create Business'}
                </button>
            </div>
        </form>
    );
};

export default Business;
